import { useState, useEffect } from "react";
import { Route, Routes, useParams, Link, useNavigate, useLocation } from "react-router-dom";

import { Space, Breadcrumb, Card, Button, Menu } from "antd";
import { PageHeader } from "@ant-design/pro-layout";  
import {
  Site, DeviceData, DeviceProfile
} from "../../model/model";

import SessionStore from "../../model/SessionStore";
import DeleteConfirm from "../../components/DeleteConfirm";
import DeviceDashboard from "./DeviceDashboard";
import EditDevice from "./EditDevice";
import ConfigDevice from "./ConfigDevice";
import DeviceSwitches from "./DeviceSwitches";

interface IProps {
  site: Site;
}

function DeviceLayout(props: IProps) {
  const { devId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [deviceData, setDevice] = useState<DeviceData | undefined>(undefined);
  const [deviceProfile, setDeviceProfile] = useState<DeviceProfile | undefined>(undefined);
  const [hasConfigPlugin, setHasConfigPlugin] = useState<boolean>(false);
  const [hasCoils, setHasCoils] = useState<boolean>(false);
  
  useEffect(() => {
    const loadDevice = () => {
      SessionStore.getClient().getDevice(devId!, SessionStore.getBearer()).then((resp) => {
        const dev: DeviceData = resp.data;
        setDevice(dev);
        const coilsCount = dev.coils ? dev.coils.length : 0;
        setHasCoils(coilsCount > 0);
        if (dev.devProfileId) {
          SessionStore.getClient().getDeviceProfile(dev.devProfileId, SessionStore.getBearer()).then((respDP) => {
            setDeviceProfile(respDP.data);
          });
          const supportedProfiles = ['wifigate', 'espgw'];
          setHasConfigPlugin(supportedProfiles.includes(dev.devProfileId));
        } else {
          setHasConfigPlugin(false);
        }
      });
    };

    loadDevice();

    return () => {
    };
  }, [devId, location.key]);

  const deleteDevice = () => {
    SessionStore.getClient().removeDevice(devId!, SessionStore.getBearer()).then(() => {
      navigate(`/site/${props.site.id}/devices`);
    });
  };

  if (!deviceData ) {
    return null;
  }

  const site = props.site;
 
  const path = location.pathname;
  let tab = "dashboard";

  if (path.endsWith("edit")) {
    tab = "edit";
  }else if (path.endsWith("conf")) {
    tab = "conf";
  }else if (path.endsWith("switches")) {
    tab = "switches";
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>
              <span>Sites</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>
                <Link to={`/site/${props.site.id}/dash`}>{props.site.name}</Link>
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>
                <Link to={`/site/${props.site.id}/devices`}>Devices</Link>
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>{deviceData.name}</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        title={deviceData.name}
        subTitle={`deviceData id: ${deviceData.id}`}
        extra={[
            <DeleteConfirm typ="deviceData" confirm={deviceData.name} onConfirm={deleteDevice}>
              <Button danger type="primary">
                Delete deviceData
              </Button>
            </DeleteConfirm>
        ]}
      />
      <Card>
        <Menu mode="horizontal" selectedKeys={[tab]} style={{ marginBottom: 24 }}>
          <Menu.Item key="dashboard">
            <Link to={`/site/${site.id}/device/${deviceData.id}`}>
              Dashboard
            </Link>
          </Menu.Item>
          <Menu.Item key="edit">
            <Link to={`/site/${site.id}/device/${deviceData.id}/edit`}>
              Edit
            </Link>
          </Menu.Item>
          {hasCoils && (
            <Menu.Item key="switches">
              <Link to={`/site/${site.id}/device/${deviceData.id}/switches`}>
                Switches
              </Link>
            </Menu.Item>
          )}
          {hasConfigPlugin && (
            <Menu.Item key="conf">
              <Link to={`/site/${site.id}/device/${deviceData.id}/conf`}>
                Configuration
              </Link>
            </Menu.Item>
          )}
        </Menu>
        <Routes>
          <Route path="/" element={<DeviceDashboard deviceData={deviceData}  />} />
          <Route path="/edit" element={<EditDevice deviceData={deviceData} site={site} />} />
          <Route path="/switches" element={<DeviceSwitches deviceData={deviceData} />} />
          <Route path="/conf/*" element={<ConfigDevice deviceData={deviceData} site={site} deviceProfile={deviceProfile}/>} />
        </Routes>
      </Card>
    </Space>
  );
}

export default DeviceLayout;

